import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { isApiError } from './client';

export interface FormErrors {
  /** Field path (`items.0.quantity`) → first server message for that field. */
  fields: Record<string, string>;
  /** Message for the form as a whole: field-less issues, or any non-validation failure. */
  form: string | null;
}

/**
 * Map a failed API call onto form errors. Messages stay as sent (English, from `@sbg/shared`);
 * localise them when rendering — `<FieldError>` does that through `localizeMessage`.
 */
export function toFormErrors(error: unknown): FormErrors {
  const result: FormErrors = { fields: {}, form: null };

  if (!isApiError(error)) {
    result.form =
      error instanceof Error ? error.message : 'Something went wrong. Please try again.';
    return result;
  }

  for (const issue of error.issues) {
    if (issue.path === '') {
      result.form ??= issue.message;
    } else {
      result.fields[issue.path] ??= issue.message; // first message per field wins
    }
  }
  // Not a field-level validation failure (auth, lock-out, rate limit, 5xx …): show it on the form.
  if (error.issues.length === 0) result.form = error.message;

  return result;
}

/**
 * Feed a failed API call into React Hook Form: one `setError` per field, plus `root.server` for the
 * form-level message. Returns the mapping so callers can also show a banner.
 *
 *   try { await mutateAsync(values) } catch (e) { applyFormErrors(setError, e) }
 */
export function applyFormErrors<T extends FieldValues>(
  setError: UseFormSetError<T>,
  error: unknown,
): FormErrors {
  const result = toFormErrors(error);
  for (const [name, message] of Object.entries(result.fields)) {
    setError(name as Path<T>, { type: 'server', message });
  }
  if (result.form !== null) setError('root.server', { type: 'server', message: result.form });
  return result;
}
